export const FEEDING_STATUS = {
  GREEN: 'green',     // Comió hace poco
  YELLOW: 'yellow',   // Cerca de la próxima ración
  RED: 'red',         // Atrasado / urgente
  UNKNOWN: 'unknown', // Sin lastFed (mascota nueva)
};

/**
 * Umbrales del semáforo expresados como FRACCIÓN del intervalo de la mascota.
 * Si el intervalo es 12h:
 *  - 0 a 50% del intervalo (0-6h)   → verde
 *  - 50% a 90% del intervalo (6-10.8h) → amarillo
 *  - >90% del intervalo (10.8h+)    → rojo
 *
 * Esto permite que el semáforo se adapte a la frecuencia real de cada mascota
 * sin tener umbrales hardcodeados en horas.
 */
export const FEEDING_THRESHOLDS = {
  GREEN_FRACTION: 0.5,
  YELLOW_FRACTION: 0.9,
};

/**
 * Default de horas entre comidas si la mascota no define feedingsPerDay.
 * 12h corresponde a 2 comidas diarias, que es lo más común.
 */
export const DEFAULT_FEEDING_INTERVAL_HOURS = 12;

/**
 * Niveles parciales de inicialización del stock.
 * El usuario elige uno al setear la capacidad del dispenser por primera vez.
 */
export const STOCK_INIT_LEVELS = {
  EMPTY: 0,
  QUARTER: 0.25,
  HALF: 0.5,
  THREE_QUARTERS: 0.75,
  FULL: 1.0,
};

/**
 * Tipos de log de bitácora.
 * Mantener estable: si renombramos un tipo, queda historial roto.
 */
export const LOG_TYPES = {
  FEEDING: 'feeding',         // Mascota alimentada
  MANUAL_FEEDING: 'manual_feeding', // Alimentación forzada sin stock
  REFILL: 'refill',           // Recarga de dispenser desde bolsa
  MAIN_STOCK_ADD: 'main_stock_add', // Ajuste manual del stock principal (escape hatch)
  CONFIRM_EMPTY: 'confirm_empty', // Usuario confirmó dispenser vacío
  STOCK_ADJUSTMENT: 'stock_adjustment', // Ajuste manual del stock
  BAG_OPENED: 'bag_opened',   // Se abrió una bolsa cerrada → mainStock
  BAGS_BOUGHT: 'bags_bought', // Se compraron bolsas nuevas (suma extraBagsCount)
  WATERING: 'watering',       // Mascota(s) tomaron agua (evento de grupo, payload.affectedPetIds)
  APPOINTMENT_CREATED: 'appointment_created',     // Se agendó una cita (vet/grooming/etc)
  APPOINTMENT_COMPLETED: 'appointment_completed', // Se marcó una cita como hecha
  APPOINTMENT_CANCELLED: 'appointment_cancelled', // Se canceló una cita
  NOTE: 'note',               // Nota libre del usuario
  VET_VISIT: 'vet_visit',     // Visita al veterinario
  HEALTH: 'health',           // Observación de salud
};

/**
 * Tipos de cita del Calendario. Cada uno tiene un ícono en la UI
 * (ver AppointmentForm / AppointmentCard / LogTimeline).
 */
export const APPOINTMENT_TYPES = {
  VET: 'vet',
  GROOMING: 'grooming',
  VACCINE: 'vaccine',
  DEWORMING: 'deworming',
  WALK: 'walk',
  OTHER: 'other',
};

/**
 * Opciones de "cuánto antes quiero el recordatorio" para una cita.
 * Chips fijos: 0 = sin recordatorio, valores en minutos para los demás.
 * El worker (cron cada 30 min) chequea si scheduledAt - reminderMinutes ya pasó
 * y aún no se mandó push.
 */
export const REMINDER_MINUTES_OPTIONS = [0, 15, 30, 60, 120, 1440];

/**
 * Tipos de observación de salud. Cada uno habilita campos opcionales
 * específicos en HealthRecordForm (ej. weight → weightKg; vaccine/deworming
 * → productName).
 */
export const HEALTH_RECORD_TYPES = {
  WEIGHT: 'weight',
  VACCINE: 'vaccine',
  DEWORMING: 'deworming',
  MEDICATION: 'medication',
  INJURY: 'injury',
  CHECKUP: 'checkup',
  NOTE: 'note',
};

/**
 * Estados del semáforo de hidratación. Mismo modelo que FEEDING_STATUS y
 * reutiliza los mismos thresholds (FEEDING_THRESHOLDS) — la fracción de
 * intervalo elapsed es invariante al dominio. Si en el futuro el usuario
 * pide thresholds distintos para agua, se extrae a HYDRATION_THRESHOLDS.
 */
export const HYDRATION_STATUS = {
  GREEN: 'green',
  YELLOW: 'yellow',
  RED: 'red',
  UNKNOWN: 'unknown',
};

/**
 * Opciones de frecuencia entre toma y toma para los baldes de agua.
 * Chips fijos por UX: cubren los casos típicos (gatos chicos cada 6h,
 * perros adultos cada 12-24h, animales más grandes o canilla automática
 * cada 48h). Si feedback real pide input libre, se itera.
 */
export const HYDRATION_INTERVALS = [6, 12, 24, 48];

/**
 * Modos de "abrir bolsa nueva":
 *  - PREVIOUS_EMPTY: la bolsa principal anterior se vació. mainStock pasa a bagCapacity.
 *    Si el sistema creía que había stock, se descarta y se loguea como discrepancia.
 *  - ADD_TO_CURRENT: se vierte la bolsa nueva sobre lo que quedaba. mainStock += bagCapacity.
 */
export const OPEN_BAG_MODES = {
  PREVIOUS_EMPTY: 'previous_empty',
  ADD_TO_CURRENT: 'add_to_current',
};

export const ROUTES = {
  LOGIN: '/login',
  ONBOARDING: '/onboarding',
  PETS: '/pets',
  PET_DETAIL: '/pets/:petId',
  PET_NEW: '/pets/new',
  PET_EDIT: '/pets/:petId/edit',
  LOGS: '/logs',
  INVENTORY: '/inventory',
  INVENTORY_PROJECTIONS: '/inventory/projections',
  GROUP_NEW: '/groups/new',
  GROUP_EDIT: '/groups/:groupId/edit',
  WATER_GROUP_NEW: '/water-groups/new',
  WATER_GROUP_EDIT: '/water-groups/:groupId/edit',
  CALENDAR: '/calendar',
  APPOINTMENT_NEW: '/appointments/new',
  APPOINTMENT_EDIT: '/appointments/:appointmentId/edit',
  HEALTH: '/health',
  PET_HEALTH: '/health/:petId',
  HEALTH_RECORD_NEW: '/health/:petId/records/new',
  HEALTH_RECORD_EDIT: '/health/:petId/records/:recordId/edit',
  SETTINGS: '/settings',
  PROFILE: '/profile',
};

export const COLLECTIONS = {
  USERS: 'users',
  FAMILIES: 'families',
  INVITATIONS: 'invitations',
  PETS: 'pets',
  LOGS: 'logs',
  FEEDING_GROUPS: 'feedingGroups',
  HYDRATION_GROUPS: 'hydrationGroups',
  APPOINTMENTS: 'appointments',
  HEALTH_RECORDS: 'healthRecords',
};

/**
 * Límites de validación.
 * Centralizados para usarse tanto en service (defensa) como en UI (UX).
 */
export const VALIDATION = {
  PET_NAME_MIN: 1,
  PET_NAME_MAX: 40,
  DAILY_RATION_MIN_GR: 5,
  DAILY_RATION_MAX_GR: 5000,
  FEEDINGS_PER_DAY_MIN: 1,
  FEEDINGS_PER_DAY_MAX: 8,
  DISPENSER_CAPACITY_MIN_GR: 50,
  DISPENSER_CAPACITY_MAX_GR: 20000,
  MAIN_STOCK_MAX_GR: 100000, // 100kg, cota razonable para evitar overflow
  GROUP_NAME_MIN: 1,
  GROUP_NAME_MAX: 40,
  BAG_CAPACITY_MIN_GR: 100,      // 100 g — bolsa de muestra
  BAG_CAPACITY_MAX_GR: 30000,    // 30 kg — bolsa institucional
  EXTRA_BAGS_MAX: 50,            // Cota razonable de bolsas en reserva
  DISPENSER_NAME_MIN: 1,
  DISPENSER_NAME_MAX: 30,
  MAX_DISPENSERS_PER_GROUP: 10,  // Cota razonable; doc Firestore tiene 1 MiB y arrays profundos son lentos
  LOW_STOCK_THRESHOLD_DAYS_MIN: 1,
  LOW_STOCK_THRESHOLD_DAYS_MAX: 30,
  HYDRATION_GROUP_NAME_MIN: 1,
  HYDRATION_GROUP_NAME_MAX: 40,
  APPOINTMENT_TITLE_MIN: 1,
  APPOINTMENT_TITLE_MAX: 80,
  APPOINTMENT_NOTES_MAX: 500,
  MAX_PETS_PER_APPOINTMENT: 10,
  HEALTH_RECORD_TITLE_MIN: 1,
  HEALTH_RECORD_TITLE_MAX: 80,
  HEALTH_RECORD_NOTES_MAX: 1000,
  HEALTH_RECORD_PRODUCT_NAME_MAX: 80,
  HEALTH_RECORD_WEIGHT_MIN_KG: 0.05, // 50 g — cachorros y gatos pequeños
  HEALTH_RECORD_WEIGHT_MAX_KG: 150,  // perros gigantes con margen
};

/**
 * Configuración de inventario y proyecciones (Fase 6).
 * El "umbral" es la cantidad de días por debajo de la cual mostramos un
 * badge "Comprar pronto" en el grupo. Configurable por grupo (cada hogar
 * tiene su propia tolerancia a quedarse sin stock).
 */
export const INVENTORY = {
  DEFAULT_LOW_STOCK_THRESHOLD_DAYS: 3,
};

/**
 * Configuración del aprendizaje empírico.
 */
export const LEARNING = {
  CYCLES_CAP: 50,              // Cap del array embebido (rolling window)
  MIN_CYCLES_TO_CALIBRATE: 3,  // Antes: 'learning'. Despues: 'calibrated'
  MIN_CYCLES_FOR_OUTLIERS: 5,  // Mínimo para que σ sea confiable
  OUTLIER_SIGMA_THRESHOLD: 2,  // Descartar si > 2σ
  EMA_ALPHA: 0.3,              // Peso del valor nuevo en el EMA (0.3 = los últimos pesan más)
};

export const LEARNING_STATUS = {
  LEARNING: 'learning',
  CALIBRATED: 'calibrated',
};